import React, { useEffect, useState } from "react";
import { getInvoices, createInvoice } from "../services/invoiceService";
import { Invoice } from "../types";
import { 
  Box, 
  Typography, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  Button, 
  Chip, 
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Add as AddIcon, Download } from "@mui/icons-material";
import api from "../api/api";
import { useAuth } from "../context/AuthContext";

const InvoiceList: React.FC = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState<any[]>([]);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // Check if user is superuser
  const isSuperUser = user?.role === 'superuser';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesData, schoolsData, subscriptionsData] = await Promise.all([
        getInvoices(),
        // Only fetch schools and subscriptions if user is superuser
        ...(isSuperUser ? [api.get('/schools/'), api.get('/subscriptions')] : [])
      ]);
      
      setInvoices(invoicesData as Invoice[]);
      if (isSuperUser) {
        setSchools(schoolsData.data as any[]);
        setSubscriptions(subscriptionsData.data as any[]);
      }
    } catch (err) {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvoice = async (subscriptionId: number) => {
    try {
      await api.post(`/invoices/subscription/${subscriptionId}/create-invoice`);
      setGenerateDialogOpen(false);
      fetchData(); // Refresh the list
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to generate invoice");
    }
  };

  const handleDownloadPDF = async (invoiceId: number, invoiceNumber: string) => {
    try {
      const response = await api.post(`/invoices/${invoiceId}/download-pdf`);
      
      // Decode base64 PDF data
      const pdfData = atob((response.data as any).pdf_data);
      const pdfArray = new Uint8Array(pdfData.length);
      for (let i = 0; i < pdfData.length; i++) {
        pdfArray[i] = pdfData.charCodeAt(i);
      }
      
      // Create blob and download
      const blob = new Blob([pdfArray], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = (response.data as any).filename || `invoice-${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to download PDF");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {isSuperUser ? 'All Invoices' : 'School Invoices'}
        </Typography>
        {isSuperUser && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setGenerateDialogOpen(true)}
          >
            Generate Invoice
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? <CircularProgress /> : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Invoice #</TableCell>
              {isSuperUser && <TableCell>School</TableCell>}
              <TableCell>Status</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map(inv => (
              <TableRow key={inv.id} hover>
                <TableCell>{inv.invoice_number}</TableCell>
                {isSuperUser && <TableCell>{inv.school_name || inv.school_id}</TableCell>}
                <TableCell><Chip label={inv.status} color={inv.status === "paid" ? "success" : inv.status === "overdue" ? "error" : "warning"} /></TableCell>
                <TableCell>{inv.currency} {inv.total_amount.toLocaleString()}</TableCell>
                <TableCell>{new Date(inv.due_date).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => navigate(`/invoices/${inv.id}`)}>View</Button>
                  <Button 
                    size="small" 
                    startIcon={<Download />}
                    onClick={() => handleDownloadPDF(inv.id, inv.invoice_number)}
                    sx={{ ml: 1 }}
                  >
                    Download
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Generate Invoice Dialog - Only for superusers */}
      {isSuperUser && (
        <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Generate Invoice from Subscription</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Select a subscription to generate an invoice for:
            </Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>School</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptions
                  .filter(sub => sub.status === 'active')
                  .map(sub => (
                    <TableRow key={sub.id}>
                      <TableCell>{schools.find(s => s.id === sub.school_id)?.name || sub.school_id}</TableCell>
                      <TableCell>{sub.plan?.name || 'Unknown Plan'}</TableCell>
                      <TableCell><Chip label={sub.status} color="success" size="small" /></TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleGenerateInvoice(sub.id)}
                        >
                          Generate Invoice
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default InvoiceList; 